import { SearchMode, useSearch } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ANY_MATCH,
} from "~/core/models/filters";
import type { CampaignInclusionPeriod } from "~/modules/campaign/models/CampaignInclusionPeriod";
import { CUSTOM_INPUT_PARAMETERS_NAMES } from "~/modules/campaign/models/CampaignOperationMgr.enum";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import CampaignOperationMgrService from "~/modules/campaign/services/campaignOperationMgr.service";
import { campaignInclusionPeriodsGetMapper } from "~/modules/campaign/utils/campaignInclusionPeriodsGetMapper";

export function useGetCampaign({
  organizationCode,
  uid,
}: {
  organizationCode?: number;
  uid: string;
}) {
  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.SECTORS,
      {
        key: "operationSectorCodeList",
        operator: OPERATOR_RSQL_ANY_MATCH,
        value: "",
      },
    ],
  ]);

  const results: Ref<CampaignOperationMgr | undefined> = ref();
  const error: Ref<LxcError | undefined> = ref();
  const isLoading: Ref<boolean> = ref(false);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    SearchMode.URL_SEARCH,
  );

  async function fetchData() {
    isLoading.value = true;

    if (!organizationCode) {
      throw new Error("Organization code is undefined");
    }
    const response = await CampaignOperationMgrService.find(
      uid,
      organizationCode,
      searchParams.value,
    );

    if (LxcError.check(response)) {
      results.value = undefined;
      error.value = response;
    } else {
      error.value = undefined;
      results.value = response;
      if (results.value.operationParametersList) {
        for (const operationParameters of results.value
          .operationParametersList) {
          if (
            operationParameters?.customInputParameters &&
            operationParameters.customInputParameters[
              CUSTOM_INPUT_PARAMETERS_NAMES.INCLUSION_PERIODS
            ]
          ) {
            operationParameters.customInputParameters.inclusionPeriods =
              campaignInclusionPeriodsGetMapper(
                operationParameters.customInputParameters[
                  CUSTOM_INPUT_PARAMETERS_NAMES.INCLUSION_PERIODS
                ] as CampaignInclusionPeriod[],
              );
          }
        }
      }
    }

    isLoading.value = false;
  }

  return {
    isLoading,
    error,
    results,
    filters,
    fetchData,
    setFilter,
    search,
    onSearch,
  };
}
